import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { FileImage, Loader2, Edit, Trash2, RotateCw, Copy, Merge, CheckCircle2 } from 'lucide-react';
import { ProcessingReceipt } from '../../../types/receipt';
import { useState, useEffect } from 'react';

interface ProcessingReceiptsProps {
  receipts: ProcessingReceipt[];
  onReviewReceipt: (receipt: ProcessingReceipt, selectedItems?: number[]) => void;
  onRemoveReceipt: (receiptId: string) => void;
  onRetryProcessing?: (receiptId: string) => void;
  onMergeItems?: (receiptId: string) => void;
}

export const ProcessingReceipts = ({
  receipts,
  onReviewReceipt,
  onRemoveReceipt,
  onRetryProcessing,
  onMergeItems
}: ProcessingReceiptsProps) => {
  // Track selected items for each receipt (receipt ID -> selected item indices)
  const [selectedItems, setSelectedItems] = useState<Record<string, number[]>>({});
  // Track which receipt is being retried
  const [retryingReceiptId, setRetryingReceiptId] = useState<string | null>(null);

  // Initialize selected items when receipts change
  useEffect(() => {
    const initialSelections: Record<string, number[]> = {};
    receipts.forEach(receipt => {
      if (receipt.extractedData?.items) {
        // Select all items by default
        initialSelections[receipt.id] = receipt.extractedData.items.map((_, idx) => idx);
      }
    });
    setSelectedItems(prev => ({ ...prev, ...initialSelections }));
  }, [receipts]);

  const toggleItemSelection = (receiptId: string, itemIndex: number) => {
    setSelectedItems(prev => {
      const currentSelections = prev[receiptId] || [];
      const isSelected = currentSelections.includes(itemIndex);

      if (isSelected) {
        return { ...prev, [receiptId]: currentSelections.filter(idx => idx !== itemIndex) };
      } else {
        return { ...prev, [receiptId]: [...currentSelections, itemIndex] };
      }
    });
  };

  const handleRetry = async (receiptId: string) => {
    if (!onRetryProcessing) return;

    setRetryingReceiptId(receiptId);
    try {
      await onRetryProcessing(receiptId);
    } finally {
      setRetryingReceiptId(null);
    }
  };

  // Clear retrying state when receipts change
  useEffect(() => {
    setRetryingReceiptId(null);
  }, [receipts]);

  if (receipts.length === 0) return null;

  const getStatusText = (status: ProcessingReceipt['status']) => {
    switch (status) {
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'Extracting data...';
      case 'completed':
        return 'Ready to review';
      case 'error':
        return 'Failed to process';
      case 'already_loaded':
        return 'Already loaded';
    }
  };

  const getStatusBadgeVariant = (status: ProcessingReceipt['status']): "default" | "secondary" | "destructive" => {
    switch (status) {
      case 'uploading':
      case 'processing':
        return 'secondary';
      case 'completed':
        return 'default';
      case 'error':
        return 'destructive';
      case 'already_loaded':
        return 'secondary';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Processing Receipts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {receipts.map((receipt) => (
          <div
            key={receipt.id}
            className="p-4 border rounded-lg bg-muted/30"
          >
            {/* Top row: filename, status badge, items count, retry */}
            <div className="flex items-center gap-3">
              <FileImage className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-medium truncate">{receipt.file.name}</span>
              <Badge variant={getStatusBadgeVariant(receipt.status)} className="text-xs">
                {getStatusText(receipt.status)}
              </Badge>
              {receipt.isDuplicate && (
                <span className="flex items-center gap-1 text-xs text-amber-600">
                  <Copy className="h-3 w-3" />
                  Already processed
                </span>
              )}
              {receipt.extractedData && receipt.extractedData.has_multiple_items && receipt.extractedData.items && (
                <>
                  <span className="text-xs text-muted-foreground">
                    {receipt.extractedData.items.length} items found
                  </span>
                  {onMergeItems && receipt.extractedData.items.length > 1 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onMergeItems(receipt.id)}
                      className="h-6 px-2 text-xs gap-1"
                    >
                      <Merge className="h-3 w-3" />
                      Merge
                    </Button>
                  )}
                </>
              )}
              {receipt.extractedData && !receipt.extractedData.has_multiple_items && (
                <span className="text-xs text-muted-foreground">
                  {receipt.extractedData.amount && `${receipt.extractedData.currency || '$'}${receipt.extractedData.amount}`}
                  {receipt.extractedData.category && ` · ${receipt.extractedData.category}`}
                </span>
              )}
              {onRetryProcessing && receipt.status === 'completed' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRetry(receipt.id)}
                  disabled={retryingReceiptId === receipt.id}
                  className="ml-auto h-7 w-7 p-0"
                >
                  {retryingReceiptId === receipt.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCw className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>

            {receipt.error && (
              <p className="text-xs text-red-500 mt-2">{receipt.error}</p>
            )}

            {/* Multi-item selection UI */}
            {receipt.status === 'completed' && receipt.extractedData?.has_multiple_items && receipt.extractedData.items && (
              <div className="mt-3 space-y-2">
                {receipt.extractedData.items.map((item, idx) => {
                  const isSelected = selectedItems[receipt.id]?.includes(idx);
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-2 p-2 rounded bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleItemSelection(receipt.id, idx)}
                        className="flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-medium truncate">{item.description}</span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-muted-foreground">{item.category}</span>
                              {item.base_amount && (
                                <span className="text-xs text-muted-foreground">
                                  Base: {item.currency} {item.base_amount}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-semibold whitespace-nowrap">
                            {item.currency} {item.amount}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onReviewReceipt(receipt, [idx])}
                          className="h-7 w-7 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onRemoveReceipt(receipt.id)}
                          className="h-7 w-7 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Single item - show just edit icon in top right */}
            {receipt.status === 'completed' && receipt.extractedData && !receipt.extractedData.has_multiple_items && (
              <div className="flex items-center justify-end gap-2 mt-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onReviewReceipt(receipt)}
                  className="h-7 w-7 p-0"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onRemoveReceipt(receipt.id)}
                  className="h-7 w-7 p-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            {receipt.status === 'already_loaded' && (
              <div className="flex items-center gap-2 mt-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  This file was already loaded as an expense for this trip
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onRemoveReceipt(receipt.id)}
                  className="ml-auto h-7 w-7 p-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            {receipt.status === 'error' && (
              <div className="flex items-center justify-end gap-2 mt-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onRemoveReceipt(receipt.id)}
                  className="h-7 w-7 p-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
